/**
 * Labels und Formatierung für die Beschäftigungshistorie.
 */

import type { EmploymentHistoryRecord, EmploymentType, WageType } from '../types';
import { EMPLOYMENT_TYPE_LABELS, WAGE_TYPE_LABELS } from '../types';

export const FEDERAL_STATE_LABELS: Record<string, string> = {
  BW: 'Baden-Württemberg',
  BY: 'Bayern',
  BE: 'Berlin',
  BB: 'Brandenburg',
  HB: 'Bremen',
  HH: 'Hamburg',
  HE: 'Hessen',
  MV: 'Mecklenburg-Vorpommern',
  NI: 'Niedersachsen',
  NW: 'Nordrhein-Westfalen',
  RP: 'Rheinland-Pfalz',
  SL: 'Saarland',
  SN: 'Sachsen',
  ST: 'Sachsen-Anhalt',
  SH: 'Schleswig-Holstein',
  TH: 'Thüringen',
};

export const HISTORY_FIELD_LABELS: Record<string, string> = {
  employmentType: 'Beschäftigungsart',
  wageType: 'Lohnart',
  hourlyWage: 'Stundenlohn',
  monthlySalary: 'Monatsgehalt',
  contractualHours: 'Vertragliche Wochenstunden',
  department: 'Abteilung',
  rv: 'RV-pflichtig',
  hansefit: 'Hansefit',
  // Settings-Felder (ab Phase 4a)
  federalState: 'Bundesland',
  vacationDaysFullTime: 'Jahresurlaub (Vollzeit)',
  workDaysPerWeekFullTime: 'Wochenarbeitstage (Vollzeit)',
  hoursPerDayFullTime: 'Sollstunden/Tag (Vollzeit)',
};

export function formatHistoryValue(field: string, value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (field === 'hourlyWage' || field === 'monthlySalary')
    return `${Number(value).toLocaleString('de-DE', { minimumFractionDigits: 2 })} \u20AC`;
  if (field === 'contractualHours')
    return `${Number(value).toLocaleString('de-DE')} Std./Woche`;
  if (field === 'rv' || field === 'hansefit') return value ? 'Ja' : 'Nein';
  if (field === 'employmentType') return EMPLOYMENT_TYPE_LABELS[value as EmploymentType] || String(value);
  if (field === 'wageType') return WAGE_TYPE_LABELS[value as WageType] || String(value);
  if (field === 'federalState') return FEDERAL_STATE_LABELS[value as string] || String(value);
  if (field === 'vacationDaysFullTime') return `${value} Tage`;
  if (field === 'workDaysPerWeekFullTime') return `${value}-Tage-Woche`;
  if (field === 'hoursPerDayFullTime') return `${Number(value).toLocaleString('de-DE')} Std./Tag`;
  return String(value);
}

/**
 * Vergleicht zwei History-Records und gibt die geänderten Felder zurück.
 */
export function getChangedFields(
  current: EmploymentHistoryRecord,
  previous: EmploymentHistoryRecord | null
): string[] {
  if (!previous) return Object.keys(HISTORY_FIELD_LABELS);
  const fields = Object.keys(HISTORY_FIELD_LABELS);
  return fields.filter((f) => {
    const curr = current[f as keyof EmploymentHistoryRecord];
    const prev = previous[f as keyof EmploymentHistoryRecord];
    return curr !== prev;
  });
}
